import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useOfferCheckContext} from 'components/Accommodation/Rooms/OfferCheckContext'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getCurrencyCode} from 'modules/meta/selectors'
import {getRoomDetailOverlayVisibility} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {OfferAccessFlow} from 'modules/sapiSearch/constants'
import {
  getRoomOfferAccessConfig,
  getRoomOfferAppLockedConfig
} from 'modules/searchApi/selectors'
import {toggle} from 'opticks'

import {getAnonymousId} from '@daedalus/core/src/_web/anonymousId'
import {Page} from '@daedalus/core/src/analytics/types/Events'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@daedalus/core/src/auth/types/Auth'
import {Offer} from '@daedalus/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@daedalus/core/src/price/business/price'
import {SplitBooking} from '@daedalus/core/src/room/types/room'
import {useLogIn} from '@daedalus/shared/src/authentication/hooks/useLogIn'

import {useGetComposedOfferCheckAPIParamsBuilder} from './useGetComposedOfferCheckAPIParams'

type Props = {
  offer: {offer?: Offer; splitBooking?: SplitBooking}
}

const NAVIGATION_DELAY = 50

export const useDefferedOfferInteraction = ({offer}: Props) => {
  const isRoomsDetailsOverlayOpen = useSelector(getRoomDetailOverlayVisibility)
  const {buildOfferCheckParams} = useGetComposedOfferCheckAPIParamsBuilder()
  const {triggerOfferCheck} = useOfferCheckContext()
  const {offer: roomOffer, splitBooking} = offer
  const dispatch = useDispatch()
  const hotelId = useSelector(getAccommodationPageHotelId)
  const currencyCode = useSelector(getCurrencyCode)
  const login = useLogIn()

  const {generateAppLockedDealDeepLink} = useGenerateAppDownloadUrl(Page.Search)

  const {appDownloadLinkId} = useSelector(state =>
    getRoomOfferAppLockedConfig(state, roomOffer?.id)
  )

  const flowType = useSelector(state =>
    getRoomOfferAccessConfig(state, roomOffer?.id)
  )

  const anonymousId = getAnonymousId()
  const defferedOfferClick = useCallback(
    (landingUrl: string) => {
      const apiParams = buildOfferCheckParams(offer)

      if (isRoomsDetailsOverlayOpen) {
        dispatch(hideOverlay())
      }

      triggerOfferCheck({
        apiParams,
        offerId: roomOffer ? roomOffer.id : splitBooking?.bundleId,
        onSuccess: ({httpRequestId}: {httpRequestId: string}) => {
          const landingUrlWithRequestId = `${landingUrl}&apRequestId=${httpRequestId}`

          // if new toggle is ON - we do auth after successful offer-check
          // [BCXp] clear this toggle in sc-154267
          if (toggle('7d6464d8-move-auth-to-ap', false, true)) {
            if (flowType === OfferAccessFlow.PrivateLockedDeal) {
              return login({
                anonymousId,
                flow: AuthenticationFlows.UnlockOverlay,
                sourceComponent: SourceComponentType.UnlockOverlay,
                unlockHotelId: hotelId
              })
            }

            if (flowType === OfferAccessFlow.AppLocked) {
              const appDownloadUrl = generateAppLockedDealDeepLink(
                appDownloadLinkId,
                {
                  hotelId,
                  clickedOfferRate: getTotalPrice(
                    getDisplayPrice(roomOffer?.prices)
                  ),
                  currencyCode
                }
              )

              window.location.href = appDownloadUrl
              return
            }

            //[BCXp] add SMS case here [sc-153213]
          }

          setTimeout(() => {
            window.location.href = landingUrlWithRequestId
          }, NAVIGATION_DELAY)
        }
      })
    },
    [
      buildOfferCheckParams,
      offer,
      isRoomsDetailsOverlayOpen,
      triggerOfferCheck,
      roomOffer,
      splitBooking?.bundleId,
      dispatch,
      flowType,
      login,
      anonymousId,
      hotelId,
      generateAppLockedDealDeepLink,
      appDownloadLinkId,
      currencyCode
    ]
  )

  return {
    defferedOfferClick
  }
}
